<script setup>
import {ref, computed, watch} from 'vue'
import {useItemIdsStore} from '@/stores/itemIds'
import {useCategoriesStore} from '@/stores/categories'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import FormControlPlainText from "@/common/form-control/FormControlPlainText.vue"

const props = defineProps({
    currentData: Object,
    field: Object,
    formId: String
})

// Pinia Stores
const itemIdsStore = useItemIdsStore()
const categoriesStore = useCategoriesStore()
const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)

// --- Local Reactive State ---
const promotions = ref([])
const categoryIds = ref([])

// --- State Helpers ---
const tableName = computed(() => formStore.tableName)
const id = computed(() => formStore.id)
const courseId = computed(() => props.currentData.courseId)

/**
 * Custom Preset Helper
 * Presets data in the specific form module, so changes aren't flagged as dirty immediately.
 */
const presetFormValue = (value) => {
    formStore.presetData({
        name: props.field.name,
        value: value
    })
}

// --- Initialization ---
if (!id.value) {
    presetFormValue([])
}

// --- Fetch Logic Replacing useAsyncState ---

// 1. Fetch Promotions based on Franchise ID
const fetchPromotions = async (newCourseId) => {
    if (!newCourseId) {
        promotions.value = []
        return
    }

    const course = await itemDataStore.get({
        tableName: 'odp_courses',
        id: newCourseId
    })

    const ids = await itemIdsStore.get({
        tableName: 'odp_promotions'
    })

    const allPromotions = await itemDataStore.preload({
        tableName: 'odp_promotions',
        ids
    })

    promotions.value = allPromotions.filter(o => o.franchiseId === course?.franchiseId)
}

// 2. Fetch Current Selected Category IDs
const fetchCategoryIds = async () => {
    const data = await categoriesStore.getTableColumnData({
        tableName: tableName.value,
        columnName: props.field.name
    })

    const filteredIds = data
        .filter(o => o.itemId === id.value)
        ?.map(o => o.catId) || []

    // Sync back to form data using custom commit/action
    presetFormValue(filteredIds)

    categoryIds.value = filteredIds
}

// --- Watchers ---

// Watch courseId to refetch promotions on change or on immediate setup
watch(courseId, (newCourseId) => {
    fetchPromotions(newCourseId)
}, {immediate: true})

// Fetch initial category IDs once component initializes
fetchCategoryIds()

// --- Methods ---
const toggleSelection = (promotionId) => {
    // Access the array from currentData (populated via the presetData commit)
    const currentList = props.currentData[props.field.name]
    if (!Array.isArray(currentList)) return

    const i = currentList.indexOf(promotionId)
    if (i === -1) {
        currentList.push(promotionId)
    } else {
        currentList.splice(i, 1)
    }
}
</script>

<template>
    <div>
        <FormControlPlainText v-if="promotions.length === 0" :form-id="formId">
            <em>No promotions available.</em>
        </FormControlPlainText>

        <div v-for="promotion in promotions" :key="promotion.id" class="radio">
            <label>
                <input
                    :checked="categoryIds?.indexOf(promotion.id) > -1"
                    type="checkbox"
                    @click="toggleSelection(promotion.id)"
                >
                {{ promotion.title }}
            </label>
        </div>
    </div>
</template>